<template>
  <div class="tool-page">
    <div class="tool-header">
      <h1>{{ $t('loanCalc.title') }}</h1>
      <p>{{ $t('loanCalc.subtitle') }}</p>
    </div>

    <div class="tool-card">
      <div class="form-grid">
        <div class="form-group">
          <label>{{ $t('loanCalc.amountLabel') }}</label>
          <div class="input-with-unit">
            <input type="number" v-model.number="loanAmount" @input="calculate" placeholder="100000" step="1000" />
            <span class="unit">{{ $t('loanCalc.yuan') }}</span>
          </div>
        </div>

        <div class="form-group">
          <label>{{ $t('loanCalc.rateLabel') }}</label>
          <div class="input-with-unit">
            <input type="number" v-model.number="annualRate" @input="calculate" placeholder="4.5" step="0.01" />
            <span class="unit">%</span>
          </div>
        </div>

        <div class="form-group">
          <label>{{ $t('loanCalc.termLabel') }}</label>
          <div class="input-with-unit">
            <input type="number" v-model.number="termMonths" @input="calculate" placeholder="12" step="1" min="1" max="360" />
            <span class="unit">{{ $t('loanCalc.months') }}</span>
          </div>
        </div>
      </div>

      <div class="quick-term">
        <span class="quick-label">{{ $t('loanCalc.quickTerm') }}</span>
        <button v-for="t in quickTerms" :key="t.value" :class="['quick-btn', { active: termMonths === t.value }]" @click="termMonths = t.value; calculate()">
          {{ t.label }}
        </button>
      </div>

      <div class="method-tabs">
        <button :class="['tab-btn', { active: method === 'equal' }]" @click="method = 'equal'; calculate()">
          {{ $t('loanCalc.methodEqual') }}
        </button>
        <button :class="['tab-btn', { active: method === 'principal' }]" @click="method = 'principal'; calculate()">
          {{ $t('loanCalc.methodPrincipal') }}
        </button>
      </div>

      <div class="summary-grid" v-if="result.monthlyPayment">
        <div class="summary-card primary">
          <span class="summary-label">{{ method === 'equal' ? $t('loanCalc.monthlyPayment') : $t('loanCalc.firstMonthPayment') }}</span>
          <span class="summary-value">¥ {{ formatNum(result.monthlyPayment) }}</span>
        </div>
        <div class="summary-card" v-if="method === 'principal' && result.lastMonthPayment">
          <span class="summary-label">{{ $t('loanCalc.lastMonthPayment') }}</span>
          <span class="summary-value">¥ {{ formatNum(result.lastMonthPayment) }}</span>
        </div>
        <div class="summary-card">
          <span class="summary-label">{{ $t('loanCalc.totalInterest') }}</span>
          <span class="summary-value">¥ {{ formatNum(result.totalInterest) }}</span>
        </div>
        <div class="summary-card">
          <span class="summary-label">{{ $t('loanCalc.totalPayment') }}</span>
          <span class="summary-value">¥ {{ formatNum(result.totalPayment) }}</span>
        </div>
      </div>

      <div class="schedule-section" v-if="schedule.length > 0">
        <div class="schedule-header">
          <h3>{{ $t('loanCalc.scheduleTitle') }}</h3>
          <button class="toggle-btn" @click="showSchedule = !showSchedule">
            {{ showSchedule ? $t('loanCalc.hide') : $t('loanCalc.show') }}
          </button>
        </div>
        <div class="schedule-table-wrapper" v-if="showSchedule">
          <table class="schedule-table">
            <thead>
              <tr>
                <th>{{ $t('loanCalc.period') }}</th>
                <th>{{ $t('loanCalc.monthlyPaymentCol') }}</th>
                <th>{{ $t('loanCalc.principalCol') }}</th>
                <th>{{ $t('loanCalc.interestCol') }}</th>
                <th>{{ $t('loanCalc.remainingCol') }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="item in schedule" :key="item.period">
                <td>{{ item.period }}</td>
                <td>{{ formatNum(item.payment) }}</td>
                <td>{{ formatNum(item.principal) }}</td>
                <td>{{ formatNum(item.interest) }}</td>
                <td>{{ formatNum(item.remaining) }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <div class="tool-card info-card">
      <h2>{{ $t('loanCalc.whatIs') }}</h2>
      <p>{{ $t('loanCalc.whatIsDesc') }}</p>
      <h3>{{ $t('loanCalc.methodsTitle') }}</h3>
      <ul>
        <li v-for="(m, i) in $t('loanCalc.methods')" :key="i">{{ m }}</li>
      </ul>
      <h3>{{ $t('loanCalc.tipsTitle') }}</h3>
      <ul>
        <li v-for="(t, i) in $t('loanCalc.tipsList')" :key="i">{{ t }}</li>
      </ul>
    </div>
  </div>
</template>

<script>
export default {
  name: 'LoanCalc',
  data() {
    return {
      loanAmount: 100000,
      annualRate: 4.5,
      termMonths: 12,
      method: 'equal',
      showSchedule: false,
      result: {},
      schedule: [],
      quickTerms: [
        { value: 3, label: '3月' },
        { value: 6, label: '6月' },
        { value: 12, label: '1年' },
        { value: 24, label: '2年' },
        { value: 36, label: '3年' },
        { value: 60, label: '5年' }
      ]
    }
  },
  methods: {
    calculate() {
      if (!this.loanAmount || !this.annualRate || !this.termMonths) {
        this.result = {}
        this.schedule = []
        return
      }

      const P = this.loanAmount
      const monthRate = this.annualRate / 100 / 12
      const N = Math.round(this.termMonths)

      if (this.method === 'equal') {
        this.calcEqual(P, monthRate, N)
      } else {
        this.calcPrincipal(P, monthRate, N)
      }
    },

    calcEqual(P, r, N) {
      // 等额本息
      if (r === 0) {
        const monthly = P / N
        this.result = {
          monthlyPayment: monthly,
          totalPayment: P,
          totalInterest: 0
        }
        this.schedule = Array.from({ length: N }, (_, i) => ({
          period: i + 1,
          payment: monthly,
          principal: monthly,
          interest: 0,
          remaining: P - monthly * (i + 1)
        }))
        return
      }
      const monthly = P * r * Math.pow(1 + r, N) / (Math.pow(1 + r, N) - 1)
      const totalPayment = monthly * N
      const totalInterest = totalPayment - P

      this.result = { monthlyPayment: monthly, totalPayment, totalInterest }

      let remaining = P
      this.schedule = []
      for (let i = 1; i <= N; i++) {
        const interest = remaining * r
        const principal = monthly - interest
        remaining -= principal
        this.schedule.push({
          period: i,
          payment: monthly,
          principal,
          interest,
          remaining: Math.max(0, remaining)
        })
      }
    },

    calcPrincipal(P, r, N) {
      // 等额本金
      const monthlyPrincipal = P / N
      let totalInterest = 0
      let remaining = P
      this.schedule = []

      for (let i = 1; i <= N; i++) {
        const interest = remaining * r
        const payment = monthlyPrincipal + interest
        totalInterest += interest
        remaining -= monthlyPrincipal
        this.schedule.push({
          period: i,
          payment,
          principal: monthlyPrincipal,
          interest,
          remaining: Math.max(0, remaining)
        })
      }

      this.result = {
        monthlyPayment: this.schedule[0]?.payment || 0,
        lastMonthPayment: this.schedule[N - 1]?.payment || 0,
        totalPayment: P + totalInterest,
        totalInterest
      }
    },

    formatNum(n) {
      if (n === null || n === undefined) return '0.00'
      return n.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',')
    }
  },
  mounted() {
    this.calculate()
  }
}
</script>

<style scoped>
.tool-page { max-width: 900px; margin: 0 auto; }
.tool-header { text-align: center; margin-bottom: 32px; }
.tool-header h1 { font-size: 28px; color: var(--text-primary); margin-bottom: 8px; }
.tool-header p { color: var(--text-secondary); font-size: 16px; }
.tool-card { background: var(--bg-card); border: 1px solid var(--border-color); border-radius: 16px; padding: 32px; margin-bottom: 24px; box-shadow: var(--shadow-sm); }

.form-grid { display: flex; gap: 16px; margin-bottom: 20px; }
.form-group { flex: 1; }
.form-group label { display: block; font-size: 14px; font-weight: 600; color: var(--text-primary); margin-bottom: 8px; }

.input-with-unit { display: flex; align-items: center; border: 1px solid var(--border-color); border-radius: 10px; background: var(--bg-input); overflow: hidden; }
.input-with-unit input { flex: 1; padding: 10px 12px; border: none; background: transparent; color: var(--text-primary); font-size: 16px; outline: none; }
.input-with-unit .unit { padding: 0 12px; color: var(--text-tertiary); font-size: 14px; border-left: 1px solid var(--border-color); background: var(--bg-tertiary); height: 100%; display: flex; align-items: center; }

.quick-term { display: flex; align-items: center; gap: 8px; margin-bottom: 20px; flex-wrap: wrap; }
.quick-label { font-size: 13px; color: var(--text-tertiary); }
.quick-btn { padding: 6px 14px; border: 1px solid var(--border-color); border-radius: 8px; background: var(--bg-input); color: var(--text-secondary); font-size: 13px; cursor: pointer; transition: all 0.2s; }
.quick-btn.active { background: var(--accent-color); color: white; border-color: var(--accent-color); }

.method-tabs { display: flex; gap: 8px; margin-bottom: 24px; }
.tab-btn { flex: 1; padding: 10px; border: 1px solid var(--border-color); border-radius: 10px; background: var(--bg-input); color: var(--text-secondary); font-size: 14px; cursor: pointer; transition: all 0.2s; }
.tab-btn.active { background: var(--accent-color); color: white; border-color: var(--accent-color); }

.summary-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(180px, 1fr)); gap: 16px; margin-bottom: 24px; }
.summary-card { background: var(--bg-tertiary); border-radius: 12px; padding: 16px; text-align: center; }
.summary-card.primary { background: var(--accent-light); }
.summary-label { display: block; font-size: 13px; color: var(--text-tertiary); margin-bottom: 4px; }
.summary-value { font-size: 20px; font-weight: 700; color: var(--text-primary); font-family: 'Courier New', monospace; }
.summary-card.primary .summary-value { color: var(--accent-color); }

.schedule-section { margin-top: 8px; }
.schedule-header { display: flex; align-items: center; justify-content: space-between; margin-bottom: 12px; }
.schedule-header h3 { font-size: 16px; color: var(--text-primary); margin: 0; }
.toggle-btn { padding: 4px 12px; border: 1px solid var(--border-color); border-radius: 6px; background: var(--bg-input); color: var(--text-secondary); font-size: 13px; cursor: pointer; }

.schedule-table-wrapper { overflow-x: auto; max-height: 400px; overflow-y: auto; }
.schedule-table { width: 100%; border-collapse: collapse; font-size: 13px; }
.schedule-table th, .schedule-table td { padding: 8px 12px; text-align: right; border-bottom: 1px solid var(--border-color); }
.schedule-table th { position: sticky; top: 0; background: var(--bg-tertiary); color: var(--text-tertiary); font-weight: 600; }
.schedule-table td { color: var(--text-secondary); font-family: 'Courier New', monospace; }
.schedule-table td:first-child, .schedule-table th:first-child { text-align: center; }

.info-card h2 { font-size: 20px; color: var(--text-primary); margin-bottom: 12px; }
.info-card h3 { font-size: 16px; color: var(--text-primary); margin: 16px 0 8px; }
.info-card p { font-size: 14px; color: var(--text-secondary); line-height: 1.8; }
.info-card ul { padding-left: 20px; margin: 0; }
.info-card li { font-size: 14px; color: var(--text-secondary); line-height: 2; }

@media (max-width: 768px) {
  .form-grid { flex-direction: column; }
  .summary-grid { grid-template-columns: 1fr 1fr; }
}
</style>
